import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NeutralHeader from "@/components/NeutralHeader";
import MinimalFooter from "@/components/MinimalFooter";
import { SECTIONS, STEPS, visibleSteps, stepTitle, stepHelper } from "@/lib/questions";
import { calculateScore } from "@/lib/api";
import { ArrowLeft, ArrowRight, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "summit_trail_answers_v1";
const INDEX_KEY = "summit_trail_index_v1";

const currency = (n) => {
  if (n === "" || n === null || n === undefined) return "";
  const num = Number(String(n).replace(/[^0-9.]/g, ""));
  if (Number.isNaN(num)) return "";
  return num.toLocaleString("en-US");
};

export default function Assessment() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
  });
  const [idx, setIdx] = useState(() => {
    const v = parseInt(localStorage.getItem(INDEX_KEY) || "0", 10);
    return Number.isFinite(v) ? v : 0;
  });
  const [submitting, setSubmitting] = useState(false);

  const steps = useMemo(() => visibleSteps(answers), [answers]);
  const safeIdx = Math.min(idx, steps.length - 1);
  const step = steps[safeIdx];
  const sectionIdx = SECTIONS.findIndex((s) => s.key === step?.section);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(answers)); }, [answers]);
  useEffect(() => { localStorage.setItem(INDEX_KEY, String(safeIdx)); }, [safeIdx]);

  const setAnswer = (id, value) => setAnswers((prev) => ({ ...prev, [id]: value }));

  const progressPct = Math.round(((safeIdx + 1) / steps.length) * 100);
  const minutesLeft = Math.max(1, Math.round(((steps.length - safeIdx) / steps.length) * 4));

  const canContinue = () => {
    if (!step) return false;
    const v = answers[step.storeAs || step.id];
    if (step.type === "number") return v !== undefined && v !== "" && Number(v) >= (step.min || 0);
    if (step.type === "radio") return !!v;
    if (step.type === "multi") return Array.isArray(v) && v.length > 0;
    if (step.type === "partner") return answers.partner === "no" || (answers.partner === "yes" && Number(answers.partner_age) > 0);
    if (step.type === "retirement_status") return !!answers.retirement_status && (answers.retirement_status === "retired" || !!answers.desired_retirement_age);
    if (step.type === "spending") {
      const m = answers.income_spending_mode;
      if (m === "amount") return Number(answers.income_spending_amount) >= 500 && Number(answers.income_spending_amount) <= 100000;
      return m === "range" || m === "unsure";
    }
    if (step.type === "income_sources_and_predictable") {
      return Array.isArray(answers.income_sources) && answers.income_sources.length > 0 &&
        (answers.income_predictable_unsure === true || (answers.income_predictable_amount !== undefined && answers.income_predictable_amount !== ""));
    }
    if (step.type === "current_expenses") {
      const m = answers.current_monthly_expenses_mode;
      if (m === "amount") return Number(answers.current_monthly_expenses_amount) >= 100 && Number(answers.current_monthly_expenses_amount) <= 200000;
      return m === "range" || m === "unsure";
    }
    return true;
  };

  const goBack = () => setIdx((i) => Math.max(0, i - 1));
  const goNext = async () => {
    if (!canContinue()) { toast.error("Please answer to continue."); return; }
    if (safeIdx < steps.length - 1) { setIdx(safeIdx + 1); return; }
    // Final: calculate
    setSubmitting(true);
    try {
      const result = await calculateScore(answers);
      sessionStorage.setItem("summit_trail_result", JSON.stringify({ answers, result }));
      navigate("/results");
    } catch (e) {
      toast.error("We couldn't calculate your score. Please try again.");
      setSubmitting(false);
    }
  };

  if (!step) return null;

  // Map internal SECTIONS to the user-facing 5-step journey shown in the mockup.
  const JOURNEY = [
    { label: "Personal Information", sectionKeys: ["profile"] },
    { label: "Financial Snapshot", sectionKeys: ["assets", "income"] },
    { label: "Planning Priorities", sectionKeys: ["taxes", "protection", "legacy"] },
    { label: "Get Your Score", sectionKeys: [] },
    { label: "Next Steps", sectionKeys: [] },
  ];
  const currentSectionKey = step?.section;
  const journeyIdx = Math.max(0, JOURNEY.findIndex((j) => j.sectionKeys.includes(currentSectionKey)));

  // Section-local step index for the "X OF Y" indicator (within the current section).
  const sameSectionSteps = steps.filter((s) => s.section === currentSectionKey);
  const localIdx = sameSectionSteps.findIndex((s) => s.id === step.id);
  const sectionOfLabel = `${localIdx + 1} OF ${sameSectionSteps.length}`;

  return (
    <div className="min-h-screen flex flex-col">
      <NeutralHeader showSubtitle={false} />

      <main id="main-content" tabIndex={-1} className="flex-1 atmo-mountain-soft" data-testid="assessment-main">
        <div className="summit-container py-10 md:py-14">

          {/* HERO HEADER */}
          <div className="text-center mb-8 md:mb-10">
            <div className="section-pill mb-5 inline-block" data-testid="assessment-eyebrow">
              Financial Future Readiness Assessment
            </div>
            <h1
              className="font-serif mx-auto"
              style={{
                color: "#1E1968",
                fontSize: "clamp(1.8rem, 3.6vw, 2.75rem)",
                fontWeight: 600,
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
                maxWidth: 820,
              }}
              data-testid="assessment-hero-title"
            >
              Let&rsquo;s get a clearer picture of your financial future.
            </h1>
            <p
              className="mt-4 text-base mx-auto"
              style={{ color: "#17233D", maxWidth: 640, lineHeight: 1.6 }}
            >
              This short assessment will help you understand where you stand today and identify areas that may deserve a closer look.
            </p>
          </div>

          {/* QUESTION CARD */}
          <div
            className="mx-auto"
            style={{
              background: "#ffffff",
              border: "1px solid #e0e8f2",
              padding: "clamp(1.25rem, 3vw, 2.5rem)",
              boxShadow: "0 10px 28px rgba(23, 35, 61, 0.06)",
              maxWidth: 920,
            }}
            data-testid="assessment-card"
          >
            {/* 5-STEP JOURNEY INDICATOR */}
            <div className="hidden md:block" data-testid="journey-desktop">
              <ol
                className="relative"
                style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", listStyle: "none", padding: 0, margin: 0, columnGap: 0 }}
                aria-label="Assessment overall progress"
              >
                {JOURNEY.map((j, i) => (
                  <li key={j.label} className="relative flex flex-col items-center text-center" style={{ padding: "0 4px" }}>
                    {i > 0 && (
                      <span
                        aria-hidden="true"
                        style={{ position: "absolute", top: 22, left: "calc(-50% + 24px)", right: "calc(50% + 24px)", height: 2, background: i <= journeyIdx ? "#258FC4" : "#dbe6f3" }}
                      />
                    )}
                    <span
                      className={"step-dot " + (i < journeyIdx ? "done" : i === journeyIdx ? "active" : "")}
                      aria-current={i === journeyIdx ? "step" : undefined}
                    >
                      {i + 1}
                    </span>
                    <div
                      className="mt-3 text-xs"
                      style={{
                        color: i <= journeyIdx ? "#1E1968" : "#5a6b85",
                        fontWeight: i === journeyIdx ? 600 : 500,
                        lineHeight: 1.3,
                        maxWidth: 110,
                      }}
                    >
                      {j.label}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Mobile compact indicator */}
            <div
              className="md:hidden flex items-center justify-between gap-3"
              data-testid="journey-mobile"
            >
              <div style={{ color: "#173A6A", fontSize: "0.85rem", fontWeight: 600 }}>
                Step {journeyIdx + 1} of 5 — {JOURNEY[journeyIdx]?.label}
              </div>
              <div style={{ color: "#3a4d70", fontSize: "0.78rem" }}>About {minutesLeft} min</div>
            </div>
            <div
              className="md:hidden mt-3 w-full h-1.5"
              style={{ background: "#dbe6f3" }}
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Assessment progress, step ${safeIdx + 1} of ${steps.length}`}
            >
              <div style={{ width: `${progressPct}%`, height: "100%", background: "#258FC4", transition: "width 400ms ease" }} data-testid="progress-bar-fill" />
            </div>

            {/* Section pill + "X OF Y" */}
            <div className="mt-8 md:mt-10 flex items-center gap-4" data-testid="question-meta">
              <span
                className="section-pill"
                style={{ background: "#eef4fb", color: "#1E1968", fontWeight: 600 }}
              >
                {SECTIONS[sectionIdx]?.label}
              </span>
              <span className="text-xs" style={{ color: "#5a6b85", letterSpacing: "0.14em", fontWeight: 500 }}>
                {sectionOfLabel}
              </span>
              <span className="ml-auto hidden md:inline text-xs" style={{ color: "#3a4d70" }}>
                About {minutesLeft} min remaining
              </span>
            </div>

            {/* Question block */}
            <div className="mt-5 fade-in" key={step.id} data-testid={`question-step-${step.id}`}>
              <h2 className="font-serif" style={{ color: "#1E1968", fontSize: "clamp(1.35rem, 2.3vw, 1.8rem)", fontWeight: 600, lineHeight: 1.22 }}>
                {stepTitle(step, answers)}
              </h2>
              {stepHelper(step, answers) && (
                <p className="mt-3 text-base" style={{ color: "#3a4d70", lineHeight: 1.6 }}>{stepHelper(step, answers)}</p>
              )}
              <div className="mt-7">
                <StepRenderer step={step} answers={answers} setAnswer={setAnswer} />
              </div>
            </div>

            {/* Back / Continue */}
            <div className="mt-10 pt-6 flex items-center justify-between gap-4" style={{ borderTop: "1px solid #eef2f7" }}>
              <button className="btn-ghost" onClick={goBack} disabled={safeIdx === 0} data-testid="nav-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1.5" aria-hidden="true" /> Back
              </button>
              <button className="btn-primary" onClick={goNext} disabled={submitting} data-testid="nav-continue-btn">
                {safeIdx === steps.length - 1 ? (submitting ? "Calculating…" : "See My Score") : "Continue"}
                {!submitting && <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />}
            </button>
          </div>
        </div>

          {/* Educational disclosure panel */}
          <div
            className="mx-auto mt-6 flex items-start gap-4"
            style={{
              maxWidth: 920,
              background: "#eef4fb",
              border: "1px solid #cfe0f1",
              padding: "1.1rem 1.25rem",
            }}
            data-testid="educational-panel"
          >
            <span
              aria-hidden="true"
              style={{
                flex: "0 0 auto",
                width: 40, height: 40,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                background: "#ffffff", color: "#1E1968",
                borderRadius: 999, border: "1px solid #cfe0f1",
                marginTop: 2,
              }}
            >
              <GraduationCap style={{ width: 20, height: 20 }} strokeWidth={1.5} />
            </span>
            <div>
              <div className="text-sm" style={{ color: "#1E1968", fontWeight: 600, lineHeight: 1.35 }}>
                This assessment is educational, takes about 3–5 minutes, and there&rsquo;s no obligation.
              </div>
              <p className="mt-1.5 text-sm" style={{ color: "#3a4d70", lineHeight: 1.6 }}>
                Your results are based on the information you provide and are designed to help organize planning questions &mdash; not make financial recommendations.
              </p>
            </div>
          </div>

        </div>
      </main>
      <MinimalFooter />
    </div>
  );
}

function StepRenderer({ step, answers, setAnswer }) {
  const storeKey = step.storeAs || step.id;

  if (step.type === "number") {
    return (
      <div className="max-w-xs">
        <input
          type="number" min={step.min} max={step.max}
          value={answers[storeKey] ?? ""}
          onChange={(e) => setAnswer(storeKey, e.target.value === "" ? "" : Number(e.target.value))}
          className="summit-input" placeholder={step.suffix || ""}
          data-testid={`number-input-${step.id}`}
          inputMode="numeric"
        />
        {step.suffix && <div className="mt-2 text-sm" style={{ color: "#3a4d70" }}>{step.suffix}</div>}
      </div>
    );
  }

  if (step.type === "radio") {
    const selected = answers[storeKey];
    return (
      <div className="space-y-3" role="radiogroup">
        {step.options.map((opt) => (
          <label key={opt.value} className={`option-row ${selected === opt.value ? "selected" : ""}`} data-testid={`radio-${step.id}-${opt.value}`}>
            <input type="radio" name={step.id} value={opt.value} checked={selected === opt.value}
              onChange={() => setAnswer(storeKey, opt.value)} className="mt-1" />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (step.type === "multi") {
    const list = answers[storeKey] || [];
    const toggle = (v) => {
      const next = list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
      setAnswer(storeKey, next);
    };
    return (
      <div className="space-y-3">
        {step.options.map((opt) => {
          const on = list.includes(opt.value);
          return (
            <label key={opt.value} className={`option-row ${on ? "selected" : ""}`} data-testid={`multi-${step.id}-${opt.value}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(opt.value)} className="mt-1" />
              <span>{opt.label}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (step.type === "partner") {
    return (
      <div className="space-y-4">
        <div className="space-y-3">
          {[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }].map((o) => (
            <label key={o.v} className={`option-row ${answers.partner === o.v ? "selected" : ""}`} data-testid={`partner-${o.v}`}>
              <input type="radio" name="partner" checked={answers.partner === o.v} onChange={() => setAnswer("partner", o.v)} />
              <span>{o.l}</span>
            </label>
          ))}
        </div>
        {answers.partner === "yes" && (
          <div>
            <label className="block text-base mb-2" style={{ color: "#173A6A", fontWeight: 500 }}>
              What is your spouse or partner's age?
            </label>
            <input type="number" min={18} max={100} value={answers.partner_age ?? ""}
              onChange={(e) => setAnswer("partner_age", e.target.value === "" ? "" : Number(e.target.value))}
              className="summit-input max-w-xs" data-testid="partner-age-input" />
          </div>
        )}
      </div>
    );
  }

  if (step.type === "retirement_status") {
    const options = [
      { v: "10plus", l: "More than 10 years from retirement" },
      { v: "6-10yr", l: "6 – 10 years from retirement" },
      { v: "3-5yr", l: "3 – 5 years from retirement" },
      { v: "<3yr", l: "Less than 3 years from retirement" },
      { v: "retired", l: "Already retired" },
      { v: "unsure", l: "I'm not sure" },
    ];
    const selected = answers.retirement_status;
    return (
      <div className="space-y-4">
        <div className="space-y-3">
          {options.map((o) => (
            <label key={o.v} className={`option-row ${selected === o.v ? "selected" : ""}`} data-testid={`retirement-status-${o.v}`}>
              <input type="radio" name="retirement_status" checked={selected === o.v} onChange={() => setAnswer("retirement_status", o.v)} />
              <span>{o.l}</span>
            </label>
          ))}
        </div>
        {selected && selected !== "retired" && (
          <div>
            <label className="block text-base mb-2" style={{ color: "#173A6A", fontWeight: 500 }}>
              At what age would you ideally like to retire?
            </label>
            <input type="number" min={40} max={90} value={answers.desired_retirement_age ?? ""}
              onChange={(e) => setAnswer("desired_retirement_age", e.target.value === "" ? "" : Number(e.target.value))}
              className="summit-input max-w-xs" data-testid="desired-retirement-age-input" />
          </div>
        )}
      </div>
    );
  }

  if (step.type === "spending") {
    const mode = answers.income_spending_mode;
    return (
      <div className="space-y-4">
        <div className="space-y-3">
          {[
            { v: "amount", l: "Enter an amount" },
            { v: "range", l: "I have a rough range but not an exact amount" },
            { v: "unsure", l: "I'm not sure" },
          ].map((o) => (
            <label key={o.v} className={`option-row ${mode === o.v ? "selected" : ""}`} data-testid={`spending-mode-${o.v}`}>
              <input type="radio" name="spending_mode" checked={mode === o.v}
                onChange={() => setAnswer("income_spending_mode", o.v)} />
              <span>{o.l}</span>
            </label>
          ))}
        </div>
        {mode === "amount" && (
          <div>
            <label className="block text-base mb-2" style={{ color: "#173A6A", fontWeight: 500 }}>
              Estimated monthly retirement spending
            </label>
            <div className="flex items-center gap-2 max-w-sm">
              <span style={{ color: "#173A6A", fontSize: "1.1rem" }}>$</span>
              <input type="text" inputMode="numeric" className="summit-input"
                value={currency(answers.income_spending_amount)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, "");
                  setAnswer("income_spending_amount", raw === "" ? "" : Number(raw));
                }}
                placeholder="e.g., 6,500"
                data-testid="spending-amount-input" />
              <span style={{ color: "#173A6A" }}>/ month</span>
            </div>
            <div className="mt-2 text-xs" style={{ color: "#3a4d70" }}>Between $500 and $100,000 per month.</div>
          </div>
        )}
      </div>
    );
  }

  if (step.type === "income_sources_and_predictable") {
    const sources = answers.income_sources || [];
    const toggle = (v) => {
      const next = sources.includes(v) ? sources.filter((x) => x !== v) : [...sources, v];
      setAnswer("income_sources", next);
    };
    const opts = [
      { v: "social_security", l: "Social Security" },
      { v: "pension", l: "Pension" },
      { v: "annuity", l: "Annuity income" },
      { v: "portfolio", l: "Portfolio withdrawals" },
      { v: "rental", l: "Rental income" },
      { v: "part_time", l: "Part-time work" },
      { v: "business", l: "Business income" },
      { v: "other", l: "Other" },
      { v: "unsure", l: "I'm not sure yet" },
    ];
    return (
      <div className="space-y-6">
        <div className="space-y-3">
          {opts.map((o) => {
            const on = sources.includes(o.v);
            return (
              <label key={o.v} className={`option-row ${on ? "selected" : ""}`} data-testid={`income-source-${o.v}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(o.v)} />
                <span>{o.l}</span>
              </label>
            );
          })}
        </div>
        <div className="pt-3 border-t" style={{ borderColor: "#eaf2fa" }}>
          <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.15rem", fontWeight: 600, lineHeight: 1.3 }}>
            Approximately how much predictable monthly income do you expect from sources designed to continue for life or for a defined retirement period?
          </div>
          <p className="mt-2 text-sm" style={{ color: "#3a4d70" }}>
            For this estimate, think primarily about Social Security, pensions, and contractual lifetime income you reasonably expect to receive.
          </p>
          <div className="mt-4 flex items-center gap-2 max-w-sm">
            <span style={{ color: "#173A6A", fontSize: "1.1rem" }}>$</span>
            <input type="text" inputMode="numeric" className="summit-input"
              disabled={answers.income_predictable_unsure}
              value={answers.income_predictable_unsure ? "" : currency(answers.income_predictable_amount)}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, "");
                setAnswer("income_predictable_amount", raw === "" ? "" : Number(raw));
              }}
              placeholder="e.g., 3,200"
              data-testid="predictable-income-input" />
            <span style={{ color: "#173A6A" }}>/ month</span>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer" data-testid="predictable-unsure-toggle">
            <input type="checkbox" checked={!!answers.income_predictable_unsure}
              onChange={(e) => setAnswer("income_predictable_unsure", e.target.checked)} />
            <span style={{ color: "#173A6A" }}>I'm not sure</span>
          </label>
        </div>
      </div>
    );
  }

  if (step.type === "current_expenses") {
    const mode = answers.current_monthly_expenses_mode;
    return (
      <div className="space-y-4">
        <div className="space-y-3">
          {[
            { v: "amount", l: "Enter an amount" },
            { v: "range", l: "I have a rough range but not an exact amount" },
            { v: "unsure", l: "I'm not sure" },
          ].map((o) => (
            <label key={o.v} className={`option-row ${mode === o.v ? "selected" : ""}`} data-testid={`current-expenses-mode-${o.v}`}>
              <input type="radio" name="current_expenses_mode" checked={mode === o.v}
                onChange={() => setAnswer("current_monthly_expenses_mode", o.v)} />
              <span>{o.l}</span>
            </label>
          ))}
        </div>
        {mode === "amount" && (
          <div>
            <label className="block text-base mb-2" style={{ color: "#173A6A", fontWeight: 500 }}>Estimated current monthly spending</label>
            <div className="flex items-center gap-2 max-w-sm">
              <span style={{ color: "#173A6A", fontSize: "1.1rem" }}>$</span>
              <input type="text" inputMode="numeric" className="summit-input"
                value={currency(answers.current_monthly_expenses_amount)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, "");
                  setAnswer("current_monthly_expenses_amount", raw === "" ? "" : Number(raw));
                }}
                placeholder="e.g., 5,200"
                data-testid="current-expenses-amount-input" />
              <span style={{ color: "#173A6A" }}>/ month</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
